import type { VignetteDefinition, VignetteRuntimeContext } from "./vignette-defs/types";

interface PendingCue {
  id: string;
  offsetMs: number;
}

/**
 * Orchestrates one vignette's playback: frames the camera, drives Johnny's
 * animation, fires audio cues at their offsets, and returns everything to
 * idle when done. One instance handles vignettes one at a time — Scheduler
 * only asks for a next vignette while `isPlaying()` is false.
 */
export class VignettePlayer {
  private readonly ctx: VignetteRuntimeContext;
  private playing: VignetteDefinition | null = null;
  private elapsedMs = 0;
  private durationMs = Infinity;
  private pendingCues: PendingCue[] = [];

  constructor(ctx: VignetteRuntimeContext) {
    this.ctx = ctx;
  }

  isPlaying(): boolean {
    return this.playing !== null;
  }

  getCurrent(): VignetteDefinition | null {
    return this.playing;
  }

  play(vignette: VignetteDefinition): void {
    this.playing = vignette;
    this.elapsedMs = 0;
    this.durationMs = vignette.durationMs ?? Infinity;
    this.pendingCues = [...(vignette.audioCues ?? [])];

    if (vignette.cameraFraming) {
      this.ctx.cameraRig.frameOn(this.ctx.johnny.root.position, vignette.cameraFraming);
    }

    this.ctx.johnny.animation.playAction(vignette.animationClip, {
      loop: vignette.loopAnimation ?? false,
      onComplete: vignette.loopAnimation ? undefined : () => this.finish(),
    });

    vignette.onEnter?.(this.ctx);
  }

  /** Call every frame; a no-op while nothing is playing. */
  update(deltaMs: number): void {
    if (!this.playing) return;
    this.elapsedMs += deltaMs;

    for (let i = this.pendingCues.length - 1; i >= 0; i--) {
      if (this.elapsedMs >= this.pendingCues[i].offsetMs) {
        this.ctx.audio.playCue(this.pendingCues[i].id);
        this.pendingCues.splice(i, 1);
      }
    }

    // Primarily for looping clips (which have no natural end and so
    // *require* an explicit durationMs); doubles as a safety-net timeout
    // for a non-looping clip in case its onComplete never fires.
    if (this.elapsedMs >= this.durationMs) this.finish();
  }

  /** Force-ends whatever's currently playing without waiting for it to finish naturally — used by debug overrides. */
  cancelCurrent(): void {
    this.finish();
  }

  private finish(): void {
    const vignette = this.playing;
    if (!vignette) return; // already finished (e.g. onComplete and the durationMs timeout both fired)
    this.playing = null;

    vignette.onExit?.(this.ctx);
    this.ctx.johnny.animation.playIdle();
    this.ctx.cameraRig.returnToDefault();
  }
}
